"use client";

import { useState } from "react";
import clsx from "clsx";
import { OrdersTable, type Order } from "./OrdersTable";
import { TradesTable, type Trade } from "./TradesTable";

type TabId = "open-orders" | "order-history" | "trade-history";

interface HistoryTabsProps {
  orders: Order[];
  trades: Trade[];
  onCancelOrder?: (orderId: string) => void;
  isLoading?: boolean;
}

const tabs: { id: TabId; label: string }[] = [
  { id: "open-orders", label: "Open Orders" },
  { id: "order-history", label: "Order History" },
  { id: "trade-history", label: "Trade History" },
];

export const HistoryTabs = ({
  orders,
  trades,
  onCancelOrder,
  isLoading = false,
}: HistoryTabsProps) => {
  const [activeTab, setActiveTab] = useState<TabId>("open-orders");

  const openOrdersCount = orders.filter(
    (o) =>
      o.status === "open" || o.status === "pending" || o.status === "partially_filled"
  ).length;

  return (
    <div className="space-y-4">
      {/* Tab Headers */}
      <div className="flex gap-1 rounded-lg bg-slate-800/50 p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              "relative flex-1 rounded-md px-3 py-2 text-xs font-medium transition",
              activeTab === tab.id
                ? "bg-slate-700 text-white"
                : "text-slate-400 hover:text-white"
            )}
          >
            {tab.label}
            {tab.id === "open-orders" && openOrdersCount > 0 && (
              <span className="ml-1 rounded-full bg-blue-500 px-1.5 py-0.5 text-[10px] text-white">
                {openOrdersCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === "open-orders" && (
          <OrdersTable
            orders={orders}
            onCancelOrder={onCancelOrder}
            isLoading={isLoading}
            showOpenOnly={true}
          />
        )}
        {activeTab === "order-history" && (
          <OrdersTable orders={orders} isLoading={isLoading} showOpenOnly={false} />
        )}
        {activeTab === "trade-history" && (
          <TradesTable trades={trades} isLoading={isLoading} />
        )}
      </div>
    </div>
  );
};
